import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { Toggle } from '@design/components/toggle/Toggle';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useInsightFeedFilters } from '@terminal/stores/use-insight-feed-filters/useInsightFeedFilters';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { widgetPaths } from '@terminal/views/widgets/widget-utils';
import { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WatchlistCard } from './components/watchlist-card/WatchlistCard';
import styles from './WatchlistTab.module.scss';

export interface WatchlistTabProps {
  trackingSource?: PosthogTrackingSource;
}

export const WatchlistTab = ({ trackingSource }: WatchlistTabProps) => {
  const { t } = useTranslation(['stream', 'whatsNew']);
  const { setQueryParams } = useQueryParams();
  const allWatchlists = useWatchlist(state => state.lists);

  const {
    isInitialized,
    initializeFromStorage,
    selectedWatchlistIds,
    setSelectedWatchlistIds,
    isSecondRelationshipEnabled,
    setIsSecondRelationshipEnabled,
  } = useInsightFeedFilters(state => ({
    isInitialized: state.isInitialized,
    initializeFromStorage: state.initializeFromStorage,
    selectedWatchlistIds: state.selectedWatchlistIds,
    setSelectedWatchlistIds: state.setSelectedWatchlistIds,
    isSecondRelationshipEnabled: state.isSecondRelationshipEnabled,
    setIsSecondRelationshipEnabled: state.setIsSecondRelationshipEnabled,
  }));

  useEffect(() => {
    if (!isInitialized) {
      initializeFromStorage();
    }
  }, [initializeFromStorage, isInitialized]);

  const handleToggleWatchlist = (id: string) => {
    const current = useInsightFeedFilters.getState().selectedWatchlistIds;
    const next = new Set(current);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    setSelectedWatchlistIds(next);
  };

  const handleCreateWatchlist = () => {
    setQueryParams({ [queryKeys.widget]: widgetPaths.watchlistManagerModal });
  };

  const areAllWatchlistsSelected =
    allWatchlists.length > 0 &&
    allWatchlists.every(list => selectedWatchlistIds.has(list.id));
  const areSomeWatchlistsSelected = allWatchlists.some(list =>
    selectedWatchlistIds.has(list.id)
  );

  const handleToggleAllWatchlist = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectedWatchlistIds(
      checked ? new Set(allWatchlists.map(list => list.id)) : new Set()
    );
  };

  return (
    <div className={styles.root} data-testid="manage-following-watchlist-tab">
      {allWatchlists.length > 0 && (
        <>
          <div className={styles.list}>
            <CheckBox
              className={styles.watchlistsAllCheckbox}
              name="watchlist-all-watchlists"
              id="watchlist-settings-all-watchlists"
              label={
                <p className={styles.watchlistsAllLabel}>
                  {t('whatsNew:drawer.allWatchlists')}
                </p>
              }
              checked={areSomeWatchlistsSelected}
              partial={areSomeWatchlistsSelected && !areAllWatchlistsSelected}
              onChange={(_, e) => handleToggleAllWatchlist(e)}
            />
            {allWatchlists.map(list => (
              <WatchlistCard
                key={list.id}
                watchlist={list}
                checked={selectedWatchlistIds.has(list.id)}
                onToggle={() => handleToggleWatchlist(list.id)}
                trackingSource={trackingSource}
              />
            ))}
          </div>
          <div className={styles.competitorsRow}>
            <Toggle
              className={styles.competitorsToggle}
              leftLabel={t('whatsNew:drawer.includeWatchlistCompetitors')}
              isChecked={isSecondRelationshipEnabled}
              onToggle={checked => setIsSecondRelationshipEnabled(checked)}
              data-testid="whats-new-include-competitors-toggle"
            />
          </div>
        </>
      )}
      <ButtonLink
        className={styles.createWatchlistButton}
        iconName="Add"
        label={t('stream:manageFollowing.createWatchlist')}
        onClick={handleCreateWatchlist}
      />
    </div>
  );
};
